import type { AttributeValueRequest, Locale } from '@shop/types';

import type { Attribute, AttributeValue } from '../../lib/types';

/** Значение атрибута в форме: строки для полей ввода, список опций для multi_enum. */
export interface AttributeDraft {
  /** Текст: ключ '' — без языка, иначе код языка. */
  text: Partial<Record<Locale | '', string>>;
  number: string;
  bool: '' | 'true' | 'false';
  optionIds: string[];
}

export const emptyDraft = (): AttributeDraft => ({ text: {}, number: '', bool: '', optionIds: [] });

/** Значения товара (без вариантов) → черновики формы по attributeId. */
export function toDrafts(values: AttributeValue[]): Record<string, AttributeDraft> {
  const drafts: Record<string, AttributeDraft> = {};
  for (const v of values) {
    if (v.variantId) continue;
    const d = (drafts[v.attributeId] ??= emptyDraft());
    if (v.valueText !== null) d.text[v.locale ?? ''] = v.valueText;
    if (v.valueNumber !== null) d.number = String(Number(v.valueNumber));
    if (v.valueBool !== null) d.bool = v.valueBool ? 'true' : 'false';
    if (v.optionId) d.optionIds.push(v.optionId);
  }
  return drafts;
}

/**
 * Черновики → значения для API. Сервер заменяет все значения товара целиком,
 * поэтому значения вариантов передаются без изменений.
 */
export function toRequest(
  drafts: Record<string, AttributeDraft>,
  attributes: Attribute[],
  variantValues: AttributeValue[] = [],
): AttributeValueRequest[] {
  const result: AttributeValueRequest[] = [];
  for (const attribute of attributes) {
    const d = drafts[attribute.id];
    if (!d) continue;
    const attributeId = attribute.id;
    switch (attribute.type) {
      case 'text':
        for (const [locale, text] of Object.entries(d.text)) {
          if (!text.trim()) continue;
          result.push(
            attribute.isLocalized && locale
              ? { attributeId, text: text.trim(), locale: locale as Locale }
              : { attributeId, text: text.trim() },
          );
        }
        break;
      case 'number':
      case 'dimension': {
        const n = Number(d.number.replace(',', '.'));
        if (d.number.trim() && Number.isFinite(n)) result.push({ attributeId, number: n });
        break;
      }
      case 'bool':
        if (d.bool) result.push({ attributeId, bool: d.bool === 'true' });
        break;
      case 'enum':
      case 'multi_enum':
        for (const optionId of attribute.type === 'enum' ? d.optionIds.slice(0, 1) : d.optionIds) {
          result.push({ attributeId, optionId });
        }
        break;
    }
  }
  for (const v of variantValues) {
    if (!v.variantId) continue;
    result.push({
      attributeId: v.attributeId,
      variantId: v.variantId,
      ...(v.locale ? { locale: v.locale } : {}),
      ...(v.valueText !== null ? { text: v.valueText } : {}),
      ...(v.valueNumber !== null ? { number: Number(v.valueNumber) } : {}),
      ...(v.valueBool !== null ? { bool: v.valueBool } : {}),
      ...(v.optionId ? { optionId: v.optionId } : {}),
    });
  }
  return result;
}
